if (!customElements.get('product-card')) {
    const PRELOAD_LINK_DATA_ATTR = 'data-product-card-preload';

    const requestIdle = (callback) => {
        if (typeof window.requestIdleCallback === 'function') {
            return window.requestIdleCallback(callback, { timeout: 1500 });
        }

        return window.setTimeout(callback, 200);
    };

    const preloadImage = (src, srcset, sizes) => {
        if (!src) return;

        const selector = `link[rel="preload"][as="image"][href="${CSS.escape(src)}"], link[${PRELOAD_LINK_DATA_ATTR}="${CSS.escape(src)}"]`;
        if (document.head.querySelector(selector)) return;

        const elLink = document.createElement('link');
        elLink.rel = 'preload';
        elLink.as = 'image';
        elLink.href = src;
        if (srcset) elLink.setAttribute('imagesrcset', srcset);
        if (sizes) elLink.setAttribute('imagesizes', sizes);
        elLink.setAttribute(PRELOAD_LINK_DATA_ATTR, src);
        document.head.appendChild(elLink);
    };

    const preloadVariantImage = (elOptionValue) => {
        const {
            variantImageSrc,
            variantImageSrcset,
            variantImageSizes,
            variantHoverImageSrc,
            variantHoverImageSrcset,
            variantHoverImageSizes,
        } = elOptionValue.dataset;

        preloadImage(variantImageSrc, variantImageSrcset, variantImageSizes);
        preloadImage(variantHoverImageSrc, variantHoverImageSrcset, variantHoverImageSizes);
    };

    const toColorPathUrl = (productUrl) => {
        if (!productUrl) return productUrl;

        try {
            const url = new URL(productUrl, window.location.origin);
            const leftoverPathIndex = url.search.indexOf('/');
            const leftoverPath = leftoverPathIndex >= 0 ? url.search.slice(leftoverPathIndex + 1) : '';

            url.search = '';
            url.hash = '';

            if (leftoverPath) {
                url.pathname = `${url.pathname.replace(/\/$/, '')}/${leftoverPath}`;
            }

            return url.pathname;
        } catch {
            return productUrl.split('?')[0];
        }
    };

    const loadPrimaryImageInContext = (elContainer, { src, srcset, sizes }) => {
        return new Promise((resolve) => {
            const elImg = document.createElement('img');
            elImg.className = 'cover in-view is-swapping';
            elImg.loading = 'eager';

            const finish = () => {
                if (typeof elImg.decode === 'function') {
                    elImg.decode().then(() => resolve(elImg)).catch(() => resolve(elImg));
                    return;
                }

                resolve(elImg);
            };

            elImg.onerror = finish;
            elImg.onload = finish;

            if (srcset) elImg.srcset = srcset;
            if (sizes) elImg.sizes = sizes;

            elContainer.appendChild(elImg);
            elImg.src = src;

            if (elImg.complete) finish();
        });
    };

    class ProductCard extends HTMLElement {
        #swapGeneration = 0;

        constructor() {
            super();
            this.elPrice = null;
            this.elCap = null;
            this.elImage = null;
            this.elSaleTag = null;
            this.elPublicationBadge = null;
            this.elAtc = null;
            this.elAtcWaitlist = null;
            this.elOptionValues = null;
            this.labelAddToCart = '';
            this.labelSoldOut = '';
            this.observer = null;
            this.hasPreloadedVariants = false;
        }

        connectedCallback() {
            this.bind();
            this.#observeViewport();
        }

        disconnectedCallback() {
            this.observer && this.observer.disconnect();
        }

        bind() {
            this.elPrice = this.querySelector('[data-price]');
            this.elCap = this.querySelector('[data-cap]');
            this.elImage = this.querySelector('[data-image]');
            this.elSaleTag = this.querySelector('[data-sale-tag]');
            this.elPublicationBadge = this.querySelector('[data-publication-badge]');
            this.elAtc = this.querySelector('[data-atc]');
            this.elAtcWaitlist = this.querySelector('[data-atc-waitlist]');
            this.elStatus = this.querySelector('[data-status]');
            this.elOptionValues = this.querySelectorAll('[data-option-value]');
            this.labelAddToCart = this.dataset.labelAddToCart || 'Add to cart';
            this.labelSoldOut = this.dataset.labelSoldOut || 'Sold out';
            this.waitlistEnabled = Boolean(this.elAtcWaitlist);

            this.elAtc && this.elAtc.addEventListener('click', this.handleAddToCart.bind(this));

            const elSelected = this.querySelector('[data-option-value]:checked');
            if (elSelected) {
                this.#updateProductUrls(elSelected);
                this.#syncAtcFromOption(elSelected);
            }

            for (const elOptionValue of this.elOptionValues) {
                elOptionValue.addEventListener('change', this.handleOptionValueChange.bind(this));

                if (!elOptionValue.dataset.variantImageSrc) continue;

                const elSwatch = elOptionValue.closest('.product-form--swatch');
                const onHover = () => preloadVariantImage(elOptionValue);

                elOptionValue.addEventListener('mouseenter', onHover, { once: true });
                elOptionValue.addEventListener('focus', onHover, { once: true });
                elOptionValue.addEventListener('pointerdown', onHover);

                const elLabel = elSwatch && elSwatch.querySelector('label');
                if (elLabel) {
                    elLabel.addEventListener('mouseenter', onHover, { once: true });
                    elLabel.addEventListener('pointerdown', onHover);
                }
            }
        }

        handleOptionValueChange(ev) {
            this.updateVariantUi(ev.currentTarget);
        }

        handleAddToCart(ev) {
            ev.preventDefault();

            const elButton = ev.currentTarget;
            if (elButton.disabled) return;

            const variantId = this.#getVariantId();
            if (!variantId) return;

            const elSelected = this.querySelector('[data-option-value]:checked');
            const isFinalSale =
                (elSelected?.dataset.variantFinalSale || elButton.dataset.final) === 'true';
            const isPersonalizable =
                (elSelected?.dataset.variantPersonalizable || elButton.dataset.personalizable) === 'true';
            const cartData = {
                id: Number(variantId),
                quantity: 1,
                properties: {
                    ...(isFinalSale ? { '_final-sale': true, '_LPROP': 'Final Sale' } : {}),
                    ...(isPersonalizable ? { '_personalizable': true } : {}),
                },
            };

            this.classList.add('adding');
            this.elStatus && (this.elStatus.innerHTML = `<span>Adding <span class="visually-hidden">to cart</span>...</span>`);
            elButton.disabled = true;

            window.addToCart(cartData)
                .then((response) => {
                    this.classList.remove('adding');

                    if (response.status) {
                        this.classList.add('error');
                        this.elStatus && (this.elStatus.innerHTML = `<span>Error<span class="visually-hidden"> adding to cart</span></span>`);
                    } else {
                        this.classList.add('success');
                        this.elStatus && (this.elStatus.innerHTML = `<span>Added to cart</span>`);
                    }

                    setTimeout(() => {
                        this.classList.remove('error');
                        this.classList.remove('success');
                        this.#resetAtcButton(elButton);
                        setTimeout(() => {
                            this.elStatus && (this.elStatus.innerHTML = '');
                        }, 300);
                    }, 5000);
                });
        }

        #getVariantId() {
            const elSelected = this.querySelector('[data-option-value]:checked');

            if (elSelected?.dataset.variantId) {
                return elSelected.dataset.variantId;
            }

            return this.elAtc?.dataset.variantId;
        }

        #resetAtcButton(elButton) {
            const elSelected = this.querySelector('[data-option-value]:checked');

            if (elSelected) {
                this.#syncAtcAvailability(elSelected.dataset.variantAvailable === 'true');
                return;
            }

            elButton.disabled = false;
            this.elAtcWaitlist?.toggleAttribute('hidden', true);
        }

        #syncAtcAvailability(isAvailable) {
            if (this.elAtc) {
                this.elAtc.disabled = !isAvailable && !this.waitlistEnabled;
                this.elAtc.innerHTML = isAvailable ? this.labelAddToCart : this.labelSoldOut;
                this.elAtc.toggleAttribute('hidden', this.waitlistEnabled && !isAvailable);
            }

            this.elAtcWaitlist?.toggleAttribute('hidden', isAvailable);
        }

        updateVariantUi(elOptionValue) {
            const {
                variantPrice,
                variantCap,
                variantDiscount,
                variantPublication,
                variantImage,
                variantAvailable,
            } = elOptionValue.dataset;

            this.elPrice && (this.elPrice.textContent = variantPrice);
            this.elCap && this.elCap.toggleAttribute('hidden', !variantDiscount);
            this.elCap && variantDiscount && (this.elCap.textContent = variantCap);
            this.elSaleTag && this.elSaleTag.toggleAttribute('hidden', !variantDiscount);
            this.elSaleTag && variantDiscount && (this.elSaleTag.textContent = variantDiscount);
            this.elPublicationBadge && this.elPublicationBadge.toggleAttribute('hidden', !variantPublication);
            this.elPublicationBadge && variantPublication && (this.elPublicationBadge.innerHTML = JSON.parse(variantPublication));
            if (this.elImage && variantImage) {
                this.#swapVariantImage(elOptionValue);
            }

            this.#updateProductUrls(elOptionValue);
            this.#syncAtcAvailability(variantAvailable === 'true');
            this.#syncAtcFromOption(elOptionValue);
        }

        #syncAtcFromOption(elOptionValue) {
            if (!this.elAtc) return;

            const { variantFinalSale, variantPersonalizable, variantId } = elOptionValue.dataset;

            if (variantFinalSale) {
                this.elAtc.dataset.final = variantFinalSale;
            }

            if (variantPersonalizable) {
                this.elAtc.dataset.personalizable = variantPersonalizable;
            }

            if (variantId) {
                this.elAtc.dataset.variantId = variantId;
            }
        }

        async #swapVariantImage(elOptionValue) {
            const {
                variantImage,
                variantImageSrc,
                variantImageSrcset,
                variantImageSizes,
            } = elOptionValue.dataset;

            if (!variantImage || !variantImageSrc) return;

            const swapGeneration = ++this.#swapGeneration;

            preloadVariantImage(elOptionValue);

            const elNext = await loadPrimaryImageInContext(this.elImage, {
                src: variantImageSrc,
                srcset: variantImageSrcset,
                sizes: variantImageSizes,
            });

            if (swapGeneration !== this.#swapGeneration) {
                elNext.remove();
                return;
            }

            const elSelected = this.querySelector('[data-option-value]:checked');
            if (elSelected !== elOptionValue) {
                elNext.remove();
                return;
            }

            for (const elImg of this.elImage.querySelectorAll('img:not(.hover)')) {
                if (elImg !== elNext) elImg.remove();
            }

            elNext.classList.remove('is-swapping');
            this.#swapHoverMedia(variantImage);
            window.initLazyLoad?.();
        }

        #swapHoverMedia(variantImage) {
            const elTemplate = document.createElement('template');
            elTemplate.innerHTML = JSON.parse(variantImage).trim();

            for (const elMedia of this.elImage.querySelectorAll('.hover')) {
                elMedia.remove();
            }

            for (const elMedia of elTemplate.content.querySelectorAll('.hover')) {
                if (elMedia.tagName === 'IMG') {
                    elMedia.loading = 'eager';
                    elMedia.classList.add('in-view');
                }

                this.elImage.appendChild(elMedia);
            }
        }

        #updateProductUrls(elOptionValue) {
            const { productUrl } = elOptionValue.dataset;
            if (!productUrl) return;

            const nextUrl = toColorPathUrl(productUrl);

            for (const elUrl of this.querySelectorAll('[data-url]')) {
                elUrl.href = nextUrl;
            }
        }

        #observeViewport() {
            if (this.elOptionValues.length === 0) return;

            this.observer = new IntersectionObserver((entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting || this.hasPreloadedVariants) continue;

                    this.hasPreloadedVariants = true;
                    this.observer.disconnect();
                    requestIdle(() => this.#preloadVariantImages());
                }
            }, { rootMargin: '200px' });

            this.observer.observe(this);
        }

        #preloadVariantImages() {
            for (const elOptionValue of this.elOptionValues) {
                if (elOptionValue.checked || elOptionValue.disabled) continue;
                preloadVariantImage(elOptionValue);
            }
        }
    }

    customElements.define('product-card', ProductCard);
}
